/**
 * Sponsor registration — two-spine spillover only (left 2.1 bottom / right 2.2 then 2.1).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { formatApiErrorMessage } from '../utils/api-error-message';
import { useAuthStore } from '../store/auth';
import { TwoSpinePlacementPicker } from '../components/TwoSpinePlacementPicker';
import type { PlacementSelection } from '../components/PlacementTreePicker';
import {
  StarPackageSelect,
  type RegisterStarPackage,
} from '../components/global-players/GlobalTableRegistrationForm';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';

interface RegisterMemberPreview {
  star_package?: RegisterStarPackage;
  e3_enabled?: boolean;
  available_packages?: RegisterStarPackage[];
  sponsor: { username: string; invite_code: string };
  placement: { id: number; username: string };
  required: { rp: string; tp: string; queue_ap?: string; queue_usd?: string };
  balances: { rp: string; tp: string };
  sufficient: boolean;
}

export function RegisterMember() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile) as Record<string, unknown> | null;

  const rootUserId = profile?.id as number | undefined;
  const rootUsername = (profile?.username as string | undefined) ?? '—';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [starPackage, setStarPackage] = useState<RegisterStarPackage>(() => {
    const code = (profile?.level_code as string | undefined) ?? 'E2';
    if (code === 'E1') return 'E1';
    if (code === 'E3') return 'E2';
    return 'E2';
  });
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [selection, setSelection] = useState<PlacementSelection | null>(null);

  const urlPosition = useMemo(() => {
    const pos = (searchParams.get('position') ?? searchParams.get('zone') ?? '').toUpperCase();
    return pos === 'R' ? ('R' as const) : pos === 'L' ? ('L' as const) : undefined;
  }, [searchParams]);

  const previewQuery = useQuery({
    queryKey: ['register-member', 'preview', selection?.userId, starPackage],
    queryFn: async () =>
      (await usersAPI.registerMemberPreview(
        selection ? { placement_user_id: selection.userId, star_package: starPackage } : undefined,
      )) as unknown as RegisterMemberPreview,
    enabled: !!selection,
  });

  const preview = previewQuery.data;
  const availablePackages: RegisterStarPackage[] = preview?.available_packages?.length
    ? preview.available_packages
    : ['E1', 'E2'];

  useEffect(() => {
    if (!availablePackages.includes(starPackage)) {
      setStarPackage(availablePackages.includes('E2') ? 'E2' : availablePackages[0]!);
    }
  }, [availablePackages, starPackage]);

  const requiredRp = preview?.required.rp ?? '—';
  const requiredTp = preview?.required.tp ?? '—';
  const requiredFc = (preview?.required as { fc?: string } | undefined)?.fc ?? '0.05';
  const balanceRp = preview?.balances.rp ?? '0';
  const balanceTp = preview?.balances.tp ?? '0';
  const balanceFc = (preview?.balances as { fc?: string } | undefined)?.fc ?? '0';

  useEffect(() => {
    if (selection || !rootUserId || !urlPosition) return;
    setSelection({
      userId: rootUserId,
      username: rootUsername,
      position: urlPosition,
    });
  }, [rootUserId, rootUsername, selection, urlPosition]);

  const register = useMutation({
    mutationFn: () =>
      usersAPI.registerMember({
        username,
        password,
        password_confirm: passwordConfirm,
        position: selection!.position,
        placement_user_id: selection!.userId,
        star_package: starPackage,
      }),
    onSuccess: (data: { username?: string }) => {
      setMsg({
        type: 'ok',
        text: t('registerMember.success', {
          username: data?.username ?? username,
          defaultValue: `Member ${data?.username ?? username} registered successfully`,
        }),
      });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['register-member', 'preview'] });
      void qc.invalidateQueries({ queryKey: ['binary-tree'] });
      setTimeout(() => navigate('/trading', { replace: true }), 1800);
    },
    onError: (e: unknown) => {
      setMsg({
        type: 'err',
        text: formatApiErrorMessage(e, t('registerMember.failed', '注册失败')),
      });
    },
  });

  const handleSubmit = () => {
    setMsg(null);
    if (!selection) {
      setMsg({ type: 'err', text: t('registerMember.pickPlacement', '请先在网体上选择安置位置') });
      return;
    }
    if (!preview?.sufficient) {
      setMsg({ type: 'err', text: t('registerMember.insufficient', 'RP / TP / FC 余额不足') });
      return;
    }
    if (!username.trim() || username.length < 3) {
      setMsg({ type: 'err', text: '用户名至少 3 个字符' });
      return;
    }
    if (!/^[a-zA-Z0-9_.]+$/.test(username)) {
      setMsg({ type: 'err', text: '用户名仅支持英文字母、数字、下划线和点' });
      return;
    }
    if (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setMsg({ type: 'err', text: t('auth.passwordHint', '密码须含大小写与数字，至少 8 位') });
      return;
    }
    if (password !== passwordConfirm) {
      setMsg({ type: 'err', text: t('auth.passwordMismatch', '两次输入的密码不一致') });
      return;
    }
    register.mutate();
  };

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
      <ThemedPageHeader title={t('registerMember.title', 'Register New Member')} onBack={() => navigate(-1)} theme={C} />

      <main className="space-y-4 px-4 pb-28 pt-[70px]">
        <ThemedPanel className="p-4">
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            {t(
              'registerMember.subtitle',
              '选择一星/二星（及后台开启的三星）配套注册新会员：扣 RP/TP → 购买队列 + AP → 静/动奖励。只能挂登录号的左 2.1 底或右 2.2→2.1 底。',
            )}
          </p>
          <div className="mt-3">
            <StarPackageSelect
              label={t('registerForm.starLevel', '玩家星级')}
              value={starPackage}
              onChange={setStarPackage}
              available={availablePackages}
            />
          </div>
          {preview?.sponsor && (
            <div className="mt-3 rounded-xl border px-3 py-2 text-xs" style={{ borderColor: C.line, color: C.text }}>
              {t('registerMember.sponsor', 'Sponsor')}: <span className="font-semibold">{preview.sponsor.username}</span>
            </div>
          )}
        </ThemedPanel>

        <ThemedPanel className="p-4">
          <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
            {t('registerMember.treeTitle', '选择旗下安置位置')}
          </div>
          {rootUserId ? (
            <TwoSpinePlacementPicker
              rootUserId={rootUserId}
              rootUsername={rootUsername}
              selection={selection}
              onSelect={setSelection}
            />
          ) : (
            <div className="text-xs" style={{ color: C.muted }}>
              {t('common.loading', '加载中…')}
            </div>
          )}
        </ThemedPanel>

        <ThemedPanel className="p-4">
          <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
            {t('registerMember.costTitle', 'Registration cost')}
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
              <div style={{ color: C.muted }}>RP</div>
              <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                {requiredRp}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('registerMember.balance', 'Balance')}: {Number(balanceRp).toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
              <div style={{ color: C.muted }}>TP</div>
              <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                {requiredTp}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('registerMember.balance', 'Balance')}: {Number(balanceTp).toFixed(2)}
              </div>
            </div>
            <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
              <div style={{ color: C.muted }}>FC</div>
              <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                {requiredFc}
              </div>
              <div className="mt-1" style={{ color: C.muted }}>
                {t('registerMember.balance', 'Balance')}: {Number(balanceFc).toFixed(2)}
              </div>
            </div>
          </div>
          {preview && !preview.sufficient && (
            <ThemedNotice tone="error">
              {t('registerMember.insufficient', 'RP / TP / FC 余额不足')}
            </ThemedNotice>
          )}
          {preview?.required.queue_ap && (
            <div className="mt-3 rounded-xl border px-3 py-2 text-xs leading-relaxed" style={{ borderColor: C.line, color: C.muted }}>
              {t('registerMember.queueDesc', {
                ap: preview.required.queue_ap,
                usd: preview.required.queue_usd ?? '—',
                defaultValue: `注册成功后进入购买队列，排队 AP ${preview.required.queue_ap}（${preview.required.queue_usd ?? '—'}$ 配套）。`,
              })}
            </div>
          )}
        </ThemedPanel>

        <ThemedPanel className="space-y-4 p-4">
          <ThemedField label={t('auth.username', 'Username')} required>
            <ThemedInput value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" />
          </ThemedField>

          <ThemedField label={t('auth.password', 'Password')} required>
            <ThemedInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <p className="mt-1 text-[10px] leading-relaxed" style={{ color: C.muted }}>
              {t('auth.passwordHint', '密码须含大小写与数字，至少 8 位')}
            </p>
          </ThemedField>
          <ThemedField label={t('auth.confirmPassword', 'Confirm Password')} required>
            <ThemedInput type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
          </ThemedField>
        </ThemedPanel>

        {msg && (
          <ThemedNotice tone={msg.type === 'ok' ? 'success' : 'error'}>{msg.text}</ThemedNotice>
        )}
      </main>

      <ThemedBottomActionBar>
        <ThemedSubmitButton
          onClick={handleSubmit}
          disabled={
            !selection ||
            !preview?.sufficient ||
            register.isPending
          }
        >
          {register.isPending
            ? t('auth.loggingIn', 'Processing…')
            : `支付 ${requiredRp} RP + ${requiredTp} TP + ${requiredFc} FC 并注册`}
        </ThemedSubmitButton>
      </ThemedBottomActionBar>
      </div>
    </div>
  );
}
